import { observer } from 'mobx-react-lite'
import { usePropertiesPanelStore } from '../stores/usePropertiesPanelStore'
import { WoodType, ManufacturingOperationType } from '@fabrick/schema'
import { v4 as uuid } from 'uuid'
import {
  ChevronDown,
  ChevronRight,
  Plus,
  Trash2,
} from 'lucide-react'
import { ScrollArea } from './ui/scroll-area'
import { Separator } from './ui/separator'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { useState } from 'react'

export const PropertiesPanel = observer(() => {
  const panelStore = usePropertiesPanelStore()
  const selectedComponent = panelStore.selectedComponent
  const isSimpleCabinet = panelStore.isSimpleCabinet
  const resolvedCabinetConfig = panelStore.resolvedCabinetConfig

  return (
    <ScrollArea className="h-full bg-card">
      <div className="p-3">
        {selectedComponent && (
          <>
            <Separator className="my-4" />

            <div>
              <h3 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider mb-3">Properties</h3>

              {/* Name field - show prominently for groups */}
              <div className="mb-4">
                <Label htmlFor="component-name" className="text-xs text-muted-foreground mb-1.5 block">
                  {selectedComponent.type === 'group' ? 'Group Label' : 'Name'}
                </Label>
                <Input
                  id="component-name"
                  value={selectedComponent.name || ''}
                  onChange={(e) => selectedComponent.setName(e.target.value)}
                  className="h-8 text-xs"
                  placeholder={selectedComponent.type === 'group' ? 'e.g., Top Cabinets, Bottom Cabinets' : 'Component name'}
                />
              </div>

              {isSimpleCabinet && (
                <div className="mb-4">
                  <div className="text-[11px] font-semibold text-muted-foreground pb-2 mb-2 border-b border-border">Cabinet</div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Width</Label>
                    <Input
                      type="number"
                      value={resolvedCabinetConfig.width}
                      onChange={(e) => panelStore.updateCabinetWidth(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      min={0.1}
                      className="h-8 text-xs font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Height</Label>
                    <Input
                      type="number"
                      value={resolvedCabinetConfig.height}
                      onChange={(e) => panelStore.updateCabinetHeight(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      min={0.1}
                      className="h-8 text-xs font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Depth</Label>
                    <Input
                      type="number"
                      value={resolvedCabinetConfig.depth}
                      onChange={(e) => panelStore.updateCabinetDepth(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      min={0.1}
                      className="h-8 text-xs font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Door Angle</Label>
                    <Input
                      type="number"
                      value={resolvedCabinetConfig.doorOpen}
                      onChange={(e) => panelStore.updateCabinetDoorOpen(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      min={0}
                      max={Math.PI / 2}
                      className="h-8 text-xs font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Material</Label>
                    <Select
                      value={resolvedCabinetConfig.material}
                      onValueChange={(value) => panelStore.updateCabinetMaterial(value as WoodType)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.values(WoodType).map((value) => (
                          <SelectItem key={value} value={value}>
                            {value.replace(/_/g, ' ')}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <div className="text-[11px] font-semibold text-muted-foreground pb-2 mb-2 border-b border-border">Transform</div>

                <div className="mb-2">
                  <Label className="text-xs text-muted-foreground mb-1 block">Position</Label>
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      value={selectedComponent.layout.transform.position.x.toFixed(3)}
                      onChange={(e) => panelStore.updatePositionX(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                      placeholder="X"
                    />
                    <Input
                      type="number"
                      value={selectedComponent.layout.transform.position.y.toFixed(3)}
                      onChange={(e) => panelStore.updatePositionY(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                      placeholder="Y"
                    />
                    <Input
                      type="number"
                      value={selectedComponent.layout.transform.position.z.toFixed(3)}
                      onChange={(e) => panelStore.updatePositionZ(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                      placeholder="Z"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground mb-1 block">Scale</Label>
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      value={selectedComponent.layout.transform.scale.x.toFixed(3)}
                      onChange={(e) => panelStore.updateScaleX(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                      placeholder="X"
                    />
                    <Input
                      type="number"
                      value={selectedComponent.layout.transform.scale.y.toFixed(3)}
                      onChange={(e) => panelStore.updateScaleY(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                      placeholder="Y"
                    />
                    <Input
                      type="number"
                      value={selectedComponent.layout.transform.scale.z.toFixed(3)}
                      onChange={(e) => panelStore.updateScaleZ(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                      placeholder="Z"
                    />
                  </div>
                </div>
              </div>

              {selectedComponent.type === 'panel' && (selectedComponent as any).geometry && (
                <div className="mb-4">
                  <div className="text-[11px] font-semibold text-muted-foreground pb-2 mb-2 border-b border-border">Geometry</div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Width</Label>
                    <Input
                      type="number"
                      value={(selectedComponent as any).geometry?.width || 0}
                      onChange={(e) => panelStore.updateGeometryWidth(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Height</Label>
                    <Input
                      type="number"
                      value={(selectedComponent as any).geometry?.height || 0}
                      onChange={(e) => panelStore.updateGeometryHeight(parseFloat(e.target.value) || 0)}
                      step={0.01}
                      className="h-8 text-xs font-mono"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Thickness</Label>
                    <Input
                      type="number"
                      value={(selectedComponent as any).geometry?.depth || 0}
                      onChange={(e) => panelStore.updateGeometryDepth(parseFloat(e.target.value) || 0)}
                      step={0.001}
                      className="h-8 text-xs font-mono"
                    />
                  </div>
                </div>
              )}

              {selectedComponent.type === 'panel' && (selectedComponent as any).material && (
                <div className="mb-4">
                  <div className="text-[11px] font-semibold text-muted-foreground pb-2 mb-2 border-b border-border">Material</div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Type</Label>
                    <Select
                      value={(selectedComponent as any).material.type}
                      onValueChange={(value) => panelStore.updateMaterialType(value)}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wood">Wood</SelectItem>
                        <SelectItem value="metal">Metal</SelectItem>
                        <SelectItem value="plastic">Plastic</SelectItem>
                        <SelectItem value="fabric">Fabric</SelectItem>
                        <SelectItem value="glass">Glass</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Subtype</Label>
                    <Input
                      type="text"
                      value={(selectedComponent as any).material.subtype}
                      onChange={(e) => panelStore.updateMaterialSubtype(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <Label className="w-20 text-xs text-muted-foreground">Color</Label>
                    <Input
                      type="color"
                      value={(selectedComponent as any).material.color || '#D2B48C'}
                      onChange={(e) => panelStore.updateMaterialColor(e.target.value)}
                      className="h-8 w-full cursor-pointer"
                    />
                  </div>
                </div>
              )}

              {/* Manufacturing Steps Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Manufacturing Steps</h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2"
                    onClick={() => {
                      const newStep = {
                        id: uuid(),
                        operation: ManufacturingOperationType.CUT,
                        order: (selectedComponent.manufacturing_steps?.length || 0) + 1,
                        name: `Step ${(selectedComponent.manufacturing_steps?.length || 0) + 1}`,
                        description: ''
                      }
                      panelStore.addManufacturingStep(newStep)
                    }}
                  >
                    <Plus size={14} className="mr-1" />
                    Add Step
                  </Button>
                </div>

                {!selectedComponent.manufacturing_steps || selectedComponent.manufacturing_steps.length === 0 ? (
                  <div className="py-4 text-center text-xs text-muted-foreground border border-dashed border-border rounded-md">
                    No manufacturing steps
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {selectedComponent.manufacturing_steps.map((step) => (
                      <ManufacturingStepItem
                        key={step.id}
                        step={step}
                        onUpdate={(updates) => panelStore.updateManufacturingStep(step.id, updates)}
                        onDelete={() => panelStore.removeManufacturingStep(step.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </ScrollArea>
  )
})

interface ManufacturingStepItemProps {
  step: any
  onUpdate: (updates: Partial<any>) => void
  onDelete: () => void
}

const ManufacturingStepItem = observer(({ step, onUpdate, onDelete }: ManufacturingStepItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <div className="border border-border rounded-md overflow-hidden hover:border-primary/50 transition-colors">
      <div 
        className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-accent/50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-4 w-4 p-0 flex-shrink-0"
        >
          {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </Button>
        <Badge variant="secondary" className="w-6 h-5 flex items-center justify-center text-[10px] font-mono flex-shrink-0">
          {step.order}
        </Badge>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{step.name || 'Unnamed Step'}</div>
          <div className="text-xs text-muted-foreground">
            {step.operation.replace(/_/g, ' ')}
            {step.estimated_time && ` • ${step.estimated_time}min`}
            {step.tool && ` • ${step.tool}`}
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-destructive flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation()
            onDelete()
          }}
        >
          <Trash2 size={12} />
        </Button>
      </div>

      {isExpanded && (
        <div className="px-3 py-3 bg-muted/30 space-y-3 border-t border-border">
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Step Name</Label>
            <Input
              value={step.name || ''}
              onChange={(e) => onUpdate({ name: e.target.value })}
              className="h-8 text-xs"
              placeholder="Step name"
            />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Operation Type</Label>
            <Select
              value={step.operation}
              onValueChange={(value) => onUpdate({ operation: value })}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(ManufacturingOperationType).map((op) => (
                  <SelectItem key={op} value={op}>
                    {op.replace(/_/g, ' ').toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Description</Label>
            <Input
              value={step.description || ''}
              onChange={(e) => onUpdate({ description: e.target.value })}
              className="h-8 text-xs"
              placeholder="Step description"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Tool/Machine</Label>
              <Input
                value={step.tool || ''}
                onChange={(e) => onUpdate({ tool: e.target.value })}
                className="h-8 text-xs"
                placeholder="e.g., Table saw"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground mb-1.5 block">Time (min)</Label>
              <Input
                type="number"
                value={step.estimated_time || ''}
                onChange={(e) => onUpdate({ estimated_time: parseFloat(e.target.value) || undefined })}
                className="h-8 text-xs"
                placeholder="Minutes"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Notes</Label>
            <Input
              value={step.notes || ''}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              className="h-8 text-xs"
              placeholder="Additional notes"
            />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Order</Label>
            <Input
              type="number"
              value={step.order}
              onChange={(e) => onUpdate({ order: parseInt(e.target.value) || 1 })}
              className="h-8 text-xs"
              min={1}
            />
          </div>
        </div>
      )}
    </div>
  )
})
